// frontend/src/pages/Profile.jsx
// User profile self-service: update name, email, and password.
// Accessible at /profile — linked from sidebar user footer.
// commit: feat(m12): user profile page with name, email, and password change
import React, { useState, useEffect } from 'react';
import { getMe } from '../api';

const API = process.env.REACT_APP_API_URL || '';

function getToken() { return localStorage.getItem('token'); }

async function patchProfile(payload) {
  const res = await fetch(`${API}/api/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Update failed');
  return data;
}

async function patchPassword(payload) {
  const res = await fetch(`${API}/api/profile/password`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Password update failed');
  return data;
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#132e50' };

export default function Profile({ user, onUserUpdate }) {
  const [profile, setProfile] = useState(null);

  // Profile form
  const [name,      setName]      = useState('');
  const [email,     setEmail]     = useState('');
  const [profSaving, setProfSaving] = useState(false);
  const [profMsg,    setProfMsg]    = useState('');
  const [profErr,    setProfErr]    = useState('');

  // Password form
  const [currentPwd,  setCurrentPwd]  = useState('');
  const [newPwd,      setNewPwd]      = useState('');
  const [confirmPwd,  setConfirmPwd]  = useState('');
  const [showCurr,    setShowCurr]    = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConf,    setShowConf]    = useState(false);
  const [pwdSaving,   setPwdSaving]   = useState(false);
  const [pwdMsg,      setPwdMsg]      = useState('');
  const [pwdErr,      setPwdErr]      = useState('');

  useEffect(() => {
    getMe().then(u => {
      setProfile(u);
      setName(u.name || '');
      setEmail(u.email || '');
    }).catch(() => {});
  }, []);

  async function handleProfileSave(e) {
    e.preventDefault();
    setProfErr(''); setProfMsg('');
    if (!name.trim()) return setProfErr('Name is required.');
    if (!email.trim()) return setProfErr('Email is required.');
    setProfSaving(true);
    try {
      const updated = await patchProfile({ name: name.trim(), email: email.trim() });
      setProfile(prev => ({ ...prev, name: updated.name, email: updated.email }));
      if (onUserUpdate) onUserUpdate({ name: updated.name, email: updated.email });
      setProfMsg('Profile updated.');
    } catch (err) {
      setProfErr(err.message || 'Update failed.');
    } finally {
      setProfSaving(false);
    }
  }

  async function handlePasswordSave(e) {
    e.preventDefault();
    setPwdErr(''); setPwdMsg('');
    if (newPwd !== confirmPwd) return setPwdErr('New passwords do not match.');
    if (newPwd.length < 12) return setPwdErr('New password must be at least 12 characters.');
    setPwdSaving(true);
    try {
      await patchPassword({ currentPassword: currentPwd, newPassword: newPwd });
      setPwdMsg('Password updated.');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      setPwdErr(err.message || 'Password update failed.');
    } finally {
      setPwdSaving(false);
    }
  }

  const roleLabel = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  if (!profile) return <div style={{ padding: 40, color: '#9ca3af', fontSize: 13 }}>Loading…</div>;

  return (
    <div style={{ padding: '32px 40px', maxWidth: 600 }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#132e50', marginBottom: 4 }}>My Profile</div>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          {profile.org_name} · <span style={{ textTransform: 'capitalize' }}>{roleLabel(profile.role)}</span>
        </div>
      </div>

      {/* Profile details */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px 28px', marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#132e50', marginBottom: 20 }}>Personal details</div>

        {profErr && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '9px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{profErr}</div>}
        {profMsg && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '9px 14px', color: '#16a34a', fontSize: 13, marginBottom: 16 }}>{profMsg}</div>}

        <form onSubmit={handleProfileSave}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Full name *</label>
            <input value={name} onChange={e => { setName(e.target.value); setProfMsg(''); setProfErr(''); }} style={inputStyle} required />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Work email *</label>
            <input type="email" value={email} onChange={e => { setEmail(e.target.value); setProfMsg(''); setProfErr(''); }} style={inputStyle} required />
          </div>

          {/* Read-only fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Role</label>
              <div style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280', textTransform: 'capitalize' }}>
                {roleLabel(profile.role)}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Organisation</label>
              <div style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280' }}>
                {profile.org_name}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div>
              <label style={labelStyle}>Last login</label>
              <div style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280' }}>
                {profile.last_login_at
                  ? new Date(profile.last_login_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '—'}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Member since</label>
              <div style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280' }}>
                {new Date(profile.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={profSaving}
            style={{ background: profSaving ? '#9ca3af' : '#132e50', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: profSaving ? 'not-allowed' : 'pointer' }}
          >
            {profSaving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      </div>

      {/* Password change */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px 28px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#132e50', marginBottom: 20 }}>Change password</div>

        {pwdErr && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '9px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{pwdErr}</div>}
        {pwdMsg && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '9px 14px', color: '#16a34a', fontSize: 13, marginBottom: 16 }}>{pwdMsg}</div>}

        <form onSubmit={handlePasswordSave}>
          {[
            { label: 'Current password *', value: currentPwd, setter: setCurrentPwd, show: showCurr, toggleShow: setShowCurr },
            { label: 'New password * (min 12 characters)', value: newPwd, setter: setNewPwd, show: showNew, toggleShow: setShowNew },
            { label: 'Confirm new password *', value: confirmPwd, setter: setConfirmPwd, show: showConf, toggleShow: setShowConf },
          ].map(({ label, value, setter, show, toggleShow }) => (
            <div key={label} style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{label}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={show ? 'text' : 'password'}
                  value={value}
                  onChange={e => { setter(e.target.value); setPwdMsg(''); setPwdErr(''); }}
                  style={{ ...inputStyle, paddingRight: 40 }}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => toggleShow(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#6b7280', padding: 0, lineHeight: 1 }}
                >
                  {show ? '🙈' : '👁️'}
                </button>
              </div>
              {label.includes('Confirm') && confirmPwd && confirmPwd !== newPwd && (
                <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>Passwords do not match.</div>
              )}
              {label.includes('Confirm') && confirmPwd && confirmPwd === newPwd && newPwd.length >= 12 && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✓ Passwords match</div>
              )}
            </div>
          ))}

          <button
            type="submit"
            disabled={pwdSaving}
            style={{ background: pwdSaving ? '#9ca3af' : '#132e50', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: pwdSaving ? 'not-allowed' : 'pointer', marginTop: 4 }}
          >
            {pwdSaving ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}
